import { Controller } from '@hotwired/stimulus';
import { directionFromPoint, keyboardDirection, validDirection } from '../lib/direction_dial.js';

export default class extends Controller {
  static targets = ['ring', 'input'];
  static values = { value: { type: Number, default: 0 } };

  connect() {
    this._render(true);
  }

  disconnect() {
    this._releasePointer();
  }

  valueValueChanged() {
    this._render();
  }

  start(event) {
    if (event.button !== 0 || this._pointerId !== undefined) return;
    event.preventDefault();
    this._pointerId = event.pointerId;
    this._initialValue = this.valueValue;
    this.ringTarget.focus({ preventScroll: true });
    this.ringTarget.setPointerCapture(event.pointerId);
    this._setFromPointer(event);
  }

  move(event) {
    if (event.pointerId !== this._pointerId) return;
    event.preventDefault();
    this._setFromPointer(event);
  }

  finish(event) {
    if (event.pointerId !== this._pointerId) return;
    this._setFromPointer(event);
    this._releasePointer();
  }

  cancel(event) {
    if (this._pointerId === undefined || (event.pointerId !== undefined && event.pointerId !== this._pointerId)) return;
    const initialValue = this._initialValue;
    this._releasePointer();
    this._setValue(initialValue);
  }

  keydown(event) {
    if (event.key === 'Escape' && this._pointerId !== undefined) {
      event.preventDefault();
      event.stopPropagation();
      this.cancel(event);
      return;
    }
    const value = keyboardDirection(this.valueValue, event.key);
    if (value === null) return;
    event.preventDefault();
    event.stopPropagation();
    this._setValue(value);
  }

  input() {
    const value = this._inputValue();
    this.inputTarget.setAttribute('aria-invalid', !validDirection(value));
    if (validDirection(value)) this._setValue(value);
  }

  commitInput() {
    const value = this._inputValue();
    if (validDirection(value)) this._setValue(value);
    this._render(true);
  }

  inputKeydown(event) {
    if (event.key === 'Escape') {
      if (this._inputValue() !== this.valueValue) {
        event.preventDefault();
        event.stopPropagation();
        this._render(true);
      }
      // Once the field is restored, let Escape close the surrounding popover.
      return;
    }
    // Keep typing and native number-input arrows from controlling the map behind the popover.
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitInput();
    }
  }

  _inputValue() {
    return this.inputTarget.value === '' ? NaN : Number(this.inputTarget.value);
  }

  _setFromPointer(event) {
    const bounds = this.ringTarget.getBoundingClientRect();
    const value = directionFromPoint(
      { x: event.clientX, y: event.clientY },
      { x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 }
    );
    if (value !== null) this._setValue(value);
  }

  _setValue(value) {
    const rounded = Math.round(value * 100) / 100;
    if (!validDirection(rounded) || rounded === this.valueValue) return;
    this.valueValue = rounded;
    this._render();
    this.dispatch('change', { detail: { value: rounded } });
  }

  _releasePointer() {
    const pointerId = this._pointerId;
    this._pointerId = undefined;
    this._initialValue = undefined;
    if (pointerId !== undefined && this.ringTarget.hasPointerCapture(pointerId)) {
      this.ringTarget.releasePointerCapture(pointerId);
    }
  }

  _render(forceInput = false) {
    if (!this.hasRingTarget || !this.hasInputTarget || !validDirection(this.valueValue)) return;
    this.element.style.setProperty('--direction-dial-angle', `${this.valueValue}deg`);
    this.ringTarget.setAttribute('aria-valuenow', this.valueValue);
    this.ringTarget.setAttribute('aria-valuetext', `${this.valueValue} degrees clockwise from north`);
    if (forceInput || this.inputTarget.ownerDocument.activeElement !== this.inputTarget) {
      this.inputTarget.value = this.valueValue;
      this.inputTarget.removeAttribute('aria-invalid');
    }
  }
}
